import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import TShirt3D from "@/components/TShirt3D";
import Reveal from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import { ArrowRight } from "lucide-react";

// Editorial split-panel hero inspired by user reference
const HEROES = [
  {
    theme: "light", bg: "#2A231D", fg: "#F0E5D0", accent: "#D4B25C",
    line1: "PRINT YOUR", line2Prefix: "OWN ", line2Highlight: "STORY.",
    subline: "Upload a design or pick a Venvay signature — printed in India, shipped in 5 days.",
    img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/customize",
    cta: "Start designing",
  },
  {
    theme: "dark", bg: "#0A0A0A", fg: "#F5F1EB", accent: "#8B2E2E",
    line1: "TEES THAT WEAR YOUR", line2Highlight: "SIGNATURE.",
    subline: "Custom prints, oversized tees, hoodies and polos — premium cotton, editorial fits.",
    img: "https://images.unsplash.com/photo-1503341504253-dff4815485f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/men",
    cta: "Shop men",
  },
  {
    theme: "light", bg: "#3B2E22", fg: "#F0E5D0", accent: "#D4B25C",
    line1: "WEAR YOUR", line2Highlight: "INDIVIDUALITY.",
    subline: "Curated women's fits — crop tees, oversized silhouettes and boxy hoodies.",
    img: "https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/women",
    cta: "Shop women",
  },
  {
    theme: "light", bg: "#2A231D", fg: "#F0E5D0", accent: "#D4B25C",
    line1: "BULK & CORPORATE", line2Highlight: "MERCH.",
    subline: "10+ tees? We handle team merch, event drops and gift boxes — with your logo, your colors.",
    img: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/gifts",
    cta: "Get a quote",
  },
];

function HeroPanel({ h, reversed }) {
  return (
    <Link to={h.to} className="group grid grid-cols-1 md:grid-cols-2 min-h-[70vh]" style={{ background: h.bg, color: h.fg }}>
      <div className={`flex flex-col justify-center px-8 sm:px-16 py-16 ${reversed ? "md:order-2" : ""}`}>
        <p className="text-[10px] tracking-[0.4em] uppercase mb-6 opacity-70">Editorial Drop</p>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
          {h.line1}
          <br />
          {h.line2Prefix}<span style={{ color: h.accent }}>{h.line2Highlight}</span>
        </h1>
        <p className="text-sm mt-6 max-w-sm opacity-75">{h.subline || "Premium tees crafted for those who choose to stand out."}</p>
        <div className="mt-10 flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-semibold">
          <span className="border-b pb-1 transition-colors" style={{ borderColor: h.accent }}>{h.cta || "Shop the drop"}</span>
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" style={{ color: h.accent }} />
        </div>
      </div>
      <div className={`relative overflow-hidden ${reversed ? "md:order-1" : ""}`}>
        <img src={h.img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
    </Link>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useSEO({
    title: "Custom T-Shirt Printing India · Premium Tees, Hoodies & Sweatshirts",
    description: "Venvay is India's premium custom t-shirt printing brand. Shop oversized tees, hoodies, sweatshirts, shirts and jeans for men & women. Design your own or shop the Signature Collection.",
    keywords: "venvay, custom t-shirt printing india, oversized tshirt india, premium hoodies india, print your own t-shirt online, be rare be venvay",
    canonicalPath: "/",
  });
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* CINEMATIC 3D HERO */}
      <section className="relative overflow-hidden bg-[#0F0B08] text-[#F0E5D0]">
        <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20 grid md:grid-cols-2 gap-10 items-center min-h-[80vh]">
          <div className="relative z-10">
            <p className="label-tag text-[#D4B25C]/70 mb-6">Venvay · Print Studio</p>
            <h1 className="font-display font-black text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
              BE RARE.<br />
              BE <span className="text-gold">VENVAY.</span>
            </h1>
            <p className="mt-8 text-white/70 max-w-md text-lg">
              Premium custom t-shirts, hoodies & sweatshirts — designed, printed and shipped from our Indian studio.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/customize" data-testid="hero-customize-cta" className="btn-shine text-white px-8 py-4 text-xs tracking-[0.35em] uppercase font-bold inline-flex items-center gap-2 hover:scale-[1.03] transition-transform">
                Design your own <ArrowRight size={14} />
              </Link>
              <Link to="/signature" data-testid="hero-signature-cta" className="border border-[#D4B25C]/40 text-[#D4B25C] px-8 py-4 text-xs tracking-[0.35em] uppercase font-bold hover:bg-[#D4B25C] hover:text-[#0F0B08] transition-colors">
                Shop Signature
              </Link>
            </div>
            <div className="mt-10 flex gap-8 text-xs tracking-[0.25em] uppercase text-white/50">
              <div><span className="text-[#D4B25C] font-bold text-lg block">41+</span>Designs</div>
              <div><span className="text-[#D4B25C] font-bold text-lg block">5d</span>Ships in</div>
              <div><span className="text-[#D4B25C] font-bold text-lg block">4.9★</span>Rated</div>
            </div>
          </div>
          <div className="relative h-[420px] sm:h-[520px]">
            <TShirt3D />
          </div>
        </div>
      </section>

      {/* Marquee tag */}
      <div className="bg-[#8B2E2E] text-[#F0E5D0] py-3 overflow-hidden border-y border-[#D4B25C]/20 marquee-3d">
        <div className="flex animate-marquee whitespace-nowrap gap-16 text-[11px] tracking-[0.4em] uppercase font-semibold">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="flex gap-16">
              <span>Be Rare. Be Venvay.</span><span>·</span>
              <span>Buy 2 Get 1 Free</span><span>·</span>
              <span>Free Shipping ₹1499+</span><span>·</span>
              <span>New Drop — Signature Collection</span><span>·</span>
              <span>Be Rare. Be Venvay.</span><span>·</span>
              <span>Not for Everyone. Just for You.</span><span>·</span>
            </span>
          ))}
        </div>
      </div>

      {HEROES.slice(1).map((h, i) => (
        <Reveal key={i}><HeroPanel h={h} reversed={i % 2 === 0} /></Reveal>
      ))}

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="label-tag mb-3">Signature Prints</p>
              <h2 className="font-display text-4xl sm:text-5xl font-black">Elevate your <span className="text-gold">everyday</span> tee.</h2>
            </div>
            <Link to="/new" data-testid="view-all-link" className="hidden sm:flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-semibold text-[#D4B25C] hover:gap-3 transition-all">
              View new arrivals <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.slice(0, 8).map((p, idx) => (
            <Reveal key={p.id} delay={idx * 60}><ProductCard p={p} /></Reveal>
          ))}
        </div>
      </section>

      {/* Quick section links */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to="/new" data-testid="home-new-tile" className="group relative overflow-hidden rounded-md bg-[#0F0B08] text-[#F0E5D0] p-8 min-h-[180px] flex flex-col justify-end hover:-translate-y-1 transition-transform border border-[#D4B25C]/20">
            <p className="label-tag text-[#D4B25C]/70 mb-2">Fresh</p>
            <h3 className="font-display text-3xl font-black">NEW ARRIVALS<span className="text-[#D4B25C]">.</span></h3>
            <p className="text-xs text-white/60 mt-1">This week's drop</p>
          </Link>
          <Link to="/oversized" data-testid="home-oversized-tile" className="group relative overflow-hidden rounded-md bg-[#3B2E22] text-[#F0E5D0] p-8 min-h-[180px] flex flex-col justify-end hover:-translate-y-1 transition-transform border border-[#D4B25C]/20">
            <p className="label-tag mb-2">Baggy · Boxy</p>
            <h3 className="font-display text-3xl font-black">OVERSIZED EDIT<span className="text-[#D4B25C]">.</span></h3>
            <p className="text-xs text-white/60 mt-1">Drop-shoulder tees</p>
          </Link>
          <Link to="/hoodies" data-testid="home-hoodies-tile" className="group relative overflow-hidden rounded-md bg-[#2A231D] text-[#F0E5D0] p-8 min-h-[180px] flex flex-col justify-end hover:-translate-y-1 transition-transform border border-[#D4B25C]/20">
            <p className="label-tag mb-2">Winter</p>
            <h3 className="font-display text-3xl font-black">HOODIE SEASON<span className="text-[#D4B25C]">.</span></h3>
            <p className="text-xs text-white/60 mt-1">Fleece & zip-ups</p>
          </Link>
        </div>
      </section>

      {/* Bottom black conviction panel */}
      <section className="bg-[#0F0B08] text-[#F0E5D0] py-24 text-center border-y border-[#D4B25C]/10">
        <p className="label-tag text-[#D4B25C]/60 mb-6">The Manifesto</p>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-none">
          NOT FOR EVERYONE.<br />
          JUST FOR <span className="text-[#D4B25C]">YOU.</span>
        </h2>
        <Link to="/men" data-testid="cta-shop-now" className="inline-block mt-10 px-10 py-4 border border-[#D4B25C]/40 hover:bg-[#D4B25C] hover:text-[#0F0B08] hover:border-[#D4B25C] transition-colors text-xs tracking-[0.4em] uppercase font-bold text-[#D4B25C]">
          Shop the collection
        </Link>
      </section>
    </div>
  );
}
